if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (this.form) {
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
          if (this.variantIdInput && this.submitButton && !this.submitButton.disabled) {
            this.variantIdInput.disabled = false;
          }
        }

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (this.submitButton) {
          this.submitButtonText = this.getSubmitButtonTextElement();
          this.setStaticSubmitButtonText(this.submitButton.disabled ? window.variantStrings?.soldOut : undefined);
          if (document.querySelector('cart-drawer')) {
            this.submitButton.setAttribute('aria-haspopup', 'dialog');
          }
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getFormProperties() {
        const properties = {};
        if (!this.form || typeof FormData === 'undefined') return properties;

        try {
          const formData = new FormData(this.form);
          formData.forEach((value, key) => {
            const propertyMatch = key.match(/^properties\[(.+)\]$/);
            if (propertyMatch && value !== '') {
              properties[propertyMatch[1]] = value;
            }
          });
        } catch (error) {
          return properties;
        }

        return properties;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        this.error = false;
        if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        if (this.submitButton) {
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.classList.add('loading');
        }
        const spinner = this.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        // Try to find cart again if not found in constructor
        if (!this.cart) {
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        }

        const variantInput = this.form.querySelector('[name="id"]:checked') || this.form.querySelector('[name="id"]');
        const variantId = variantInput?.value;
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
        const sellingPlanInput = this.form.querySelector('[name="selling_plan"]:not(:disabled)');
        const sellingPlanId = sellingPlanInput?.value || '';
        const giftOptIn = document.getElementById(`PdpGiftOptIn-${this.form.getAttribute('id')}`);
        const giftOptions = giftOptIn?.checked
          ? Array.from(giftOptIn.closest('[data-dt-pdp-gift]').querySelectorAll('[data-gift-count-control]'))
          : [];
        const giftProductTitle = this.closest('[data-dt-pdp-ab]')?.querySelector('[data-product-title]')?.textContent.trim() || this.dataset.productTitle || 'Product';
        const additionalItems = giftOptions.flatMap(option => Array.from(option.querySelectorAll('[data-gift-count-row]')).flatMap((row, index, rows) =>
          Array.from(row.querySelectorAll('[data-gift-count]')).flatMap(input => {
            const count = Number(input.value) || 0;
            const color = input.closest('[data-gift-color]')?.dataset.giftColor;
            return count ? [{
              id: Number(input.closest('[data-gift-variant-id]').dataset.giftVariantId),
              quantity: count,
              properties: {
                'For': rows.length > 1 ? `${giftProductTitle} · ${row.querySelector('[data-gift-count-title]').textContent.trim()}` : giftProductTitle,
                ...(color ? { 'Canister color': color } : {}),
                '_gift_for_variant': String(variantId)
              }
            }] : [];
          })
        ));

        if (!variantId) {
          this.handleErrorMessage('Please select a variant');
          if (this.submitButton) {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
          }
          if (spinner) spinner.classList.add('hidden');
          return;
        }

        const isCollectionPage = document.body?.classList.contains('page-collection');

        if (!window.CartController && typeof window.dtLoadCartRuntime === 'function') {
          try {
            await window.dtLoadCartRuntime({ reason: 'add-to-cart' });
          } catch (error) {
            console.warn('[product-form] Cart runtime did not load before add to cart:', error);
          }
        }

        if (additionalItems.length && typeof window.CartController?.add !== 'function') {
          this.handleErrorMessage('Could not add festive packaging. Please try again.');
          this.submitButton?.classList.remove('loading');
          this.submitButton?.removeAttribute('aria-disabled');
          if (spinner) spinner.classList.add('hidden');
          return;
        }

        // Use CartController.add() - reliable /cart/add.js JSON API (fixes home/collection add-to-cart)
        if (window.CartController && typeof window.CartController.add === 'function') {
          if (this.cart && (!isCollectionPage || typeof this.cart.setActiveElement === 'function')) {
            this.cart.setActiveElement(document.activeElement);
          }
          const quickAddModal = this.closest('quick-add-modal');
          const refreshRoutineUpsells = Boolean(this.closest('.cart-face-towels'));
          const optimisticItem = typeof window.CartController.getOptimisticItemFromForm === 'function'
            ? window.CartController.getOptimisticItemFromForm(this.form, parseInt(variantId, 10), quantity)
            : null;
          window.CartController.add(parseInt(variantId, 10), quantity, this.getFormProperties(), { refreshRoutineUpsells, optimisticItem, sourceForm: this.form, sellingPlanId, additionalItems })
            .then((success) => {
              if (!success) {
                const visibleError = this.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message')?.textContent?.trim();
                if (!visibleError) this.handleErrorMessage(window.cartStrings?.error || 'Could not add to cart');
                this.error = true;
              } else if (quickAddModal && typeof quickAddModal.hide === 'function') {
                quickAddModal.hide(true);
              } else if (this.closest('[data-cart-hair-nudge-sheet]')) {
                this.dispatchEvent(new CustomEvent('cart-hair-nudge-sheet:added', {
                  bubbles: true,
                  detail: { form: this.form }
                }));
              }
            })
            .catch((e) => {
              console.error('[product-form] Add to cart failed:', e);
              this.handleErrorMessage(window.cartStrings?.error || 'Could not add to cart');
              this.error = true;
            })
            .finally(() => {
              if (this.submitButton) {
                this.submitButton.classList.remove('loading');
                if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              }
              if (!this.error && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (spinner) spinner.classList.add('hidden');
            });
          return;
        }

        // Fallback: original FormData flow when CartController not loaded (e.g. cart-notification theme)
        const formData = new FormData(this.form);
        const cartCanRenderSections =
          this.cart &&
          typeof this.cart.getSectionsToRender === 'function' &&
          typeof this.cart.setActiveElement === 'function' &&
          typeof this.cart.renderContents === 'function';
        if (this.cart && (!isCollectionPage || cartCanRenderSections)) {
          formData.append('sections', this.cart.getSectionsToRender().map((s) => s.id).join(','));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const doFetch = () => {
          const cfg = fetchConfig('javascript');
          cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete cfg.headers['Content-Type'];
          const fd = new FormData(this.form);
          if (this.cart && (!isCollectionPage || cartCanRenderSections)) {
            fd.append('sections', this.cart.getSectionsToRender().map((s) => s.id).join(','));
            fd.append('sections_url', window.location.pathname);
          }
          cfg.body = fd;
          return fetch(`${routes.cart_add_url}`, cfg);
        };
        const fetchWithRetry = (fn) =>
          window.cartAddFetchWithRetry ? window.cartAddFetchWithRetry(fn) : fn();
        const fetchPromise = window.cartAddQueue
          ? window.cartAddQueue(() => fetchWithRetry(doFetch.bind(this)))
          : fetchWithRetry(doFetch.bind(this));

        fetchPromise
          .then((response) => {
            if (response.status === 429) {
              this.handleErrorMessage(
                window.cartStrings?.rateLimitError || 'Too many requests. Please wait a moment and try again.'
              );
              return null;
            }
            return response.json();
          })
          .then((response) => {
            if (!response) return;
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              this.submitButton.setAttribute('aria-disabled', true);
              this.setStaticSubmitButtonText();
              this.error = true;
              return;
            } else if (!this.cart || (isCollectionPage && typeof this.cart.renderContents !== 'function')) {
              this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
              if (!this.cart || (isCollectionPage && typeof this.cart.renderContents !== 'function')) {
                if (typeof window.dtLoadCartRuntime === 'function') {
                  window.dtLoadCartRuntime({ reason: 'add-to-cart-fallback' })
                    .then(() => {
                      const drawer = document.querySelector('cart-drawer');
                      if (window.CartController && typeof window.CartController.openDrawer === 'function') {
                        window.CartController.openDrawer();
                      } else if (drawer && typeof drawer.open === 'function') {
                        drawer.open(this.submitButton);
                      } else if (drawer) {
                        drawer.classList.add('active', 'animate');
                      }
                    })
                    .catch((error) => {
                      console.warn('[ProductForm] Unable to open cart drawer after add.', error);
                    });
                } else {
                  console.warn('[ProductForm] Cart drawer was unavailable after add.');
                }
                return;
              }
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'Could not add to cart');
            this.error = true;
            if (window.CartController && typeof window.CartController.refreshCart === 'function') {
              window.CartController.refreshCart()
                .then(() => {
                  window.CartController.updateCartCount?.();
                  window.CartController.updateEmptyState?.();
                  window.CartController.updateTotals?.();
                })
                .catch(() => {});
            }
          })
          .finally(() => {
            if (this.submitButton) {
              this.submitButton.classList.remove('loading');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            }
            if (!this.error && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          this.submitButton.removeAttribute('disabled');
        }
        this.setStaticSubmitButtonText(text);
      }

      getSubmitButtonTextElement() {
        if (!this.submitButton) return null;
        const spans = Array.from(this.submitButton.querySelectorAll('span')).filter(
          (span) =>
            !span.classList.contains('sold-out-message') &&
            !span.classList.contains('icon-wrap') &&
            !span.classList.contains('loading__spinner') &&
            !span.classList.contains('visually-hidden')
        );
        return spans.find((span) => !span.querySelector('span') && span.textContent.trim().length) || spans[0] || null;
      }

      setStaticSubmitButtonText(text) {
        if (!this.submitButtonText) return;
        if (this.submitButton.hasAttribute('data-preserve-submit-text')) return;
        const soldOutText = window.variantStrings?.soldOut || 'Sold out';
        const nextText = text || window.variantStrings?.addToCart || 'Add to Cart';
        const showSoldOutMessage = nextText === soldOutText;
        const soldOutMessages = this.submitButton.querySelectorAll('.sold-out-message');
        this.submitButtonText.textContent = nextText;
        this.submitButtonText.classList.toggle('hidden', showSoldOutMessage && soldOutMessages.length > 0);
        soldOutMessages.forEach((message) => {
          message.classList.toggle('hidden', !showSoldOutMessage);
          message.textContent = showSoldOutMessage ? soldOutText : message.textContent;
        });
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
